import React, { useEffect, useRef, useState } from 'react';
import { Brain, Pause, Heart, Moon, Users, X, Check } from 'lucide-react';
import { howItWorksData } from '../../data/mock';

const HowItWorksSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const iconMap = { Brain, Pause, Heart, Moon, Users };

  return (
    <section 
      id="how-it-works"
      ref={sectionRef}
      className="py-32 bg-[#efede6] relative"
    >
      <div className="max-w-5xl mx-auto px-8">
        {/* Header */}
        <div className="text-center mb-20">
          <span className={`inline-block text-sm text-[#202020]/40 uppercase tracking-widest mb-6 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            How It Works
          </span>
          <h2 className={`text-3xl md:text-4xl lg:text-5xl font-normal text-[#202020] mb-8 tracking-tight transition-all duration-1000 delay-150 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            {howItWorksData.title}
          </h2>
          <p className={`text-xl text-[#202020]/50 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            {howItWorksData.subtitle}
          </p>
        </div>

        {/* What it's NOT */}
        <div className={`bg-white rounded-2xl p-8 mb-16 max-w-2xl mx-auto text-center transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <p className="text-[#202020]/60 leading-relaxed">
            <X className="w-4 h-4 text-[#202020]/40 inline mr-2" />
            {howItWorksData.clarification}
          </p>
        </div>

        {/* Practice Title */}
        <h3 className={`text-2xl font-normal text-[#202020] text-center mb-12 transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <Check className="w-5 h-5 text-[#202020]/50 inline mr-2" />
          {howItWorksData.practiceTitle}
        </h3>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {howItWorksData.benefits.map((benefit, index) => {
            const Icon = iconMap[benefit.icon];
            return (
              <div 
                key={index}
                className={`group bg-white rounded-2xl p-8 transition-all duration-500 hover:shadow-xl ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${(index + 5) * 80}ms` }}
              >
                <div className="w-14 h-14 rounded-2xl bg-[#efede6] flex items-center justify-center mb-6 group-hover:bg-[#202020] transition-colors duration-300">
                  <Icon className="w-6 h-6 text-[#202020]/60 group-hover:text-[#efede6] transition-colors duration-300" />
                </div>
                <p className="text-lg text-[#202020]/80">{benefit.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
